import { onScopeDispose, readonly, type Ref, ref } from "vue"

/**
 * The phone breakpoint the stylesheets already switch on (base.css,
 * utilities.css, toolbar.css). Kept here so script-side branching and CSS
 * can't drift apart.
 */
export const MOBILE_QUERY = "(max-width: 768px)"

/**
 * A reactive `matchMedia`. The query is read once — pass a literal, not a
 * value that changes — and the listener is dropped with the effect scope.
 *
 * Server/prerender and jsdom-without-matchMedia both fall through to `false`,
 * so a component using this renders its desktop branch when asked outside a
 * browser.
 */
export function useMediaQuery(query: string): Readonly<Ref<boolean>> {
  const matches = ref(false)

  if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    const list = window.matchMedia(query)
    matches.value = list.matches

    const onChange = (event: MediaQueryListEvent) => {
      matches.value = event.matches
    }
    list.addEventListener("change", onChange)
    onScopeDispose(() => list.removeEventListener("change", onChange))
  }

  return readonly(matches)
}

/** True on phone-width viewports — the app's one mobile/desktop switch. */
export function useIsMobile() {
  return useMediaQuery(MOBILE_QUERY)
}
